import React from "react";
import moment from "moment";
import {
  Toolbar,
  ToolbarGroup,
  ToolbarTitle,
  RaisedButton,
  Chip
} from "material-ui";
import { formatPrice } from "../util";

import PassTable from "./partials/pass-table.jsx";
import TicketTable from "./partials/ticket-table.jsx";
import ItemTable from "./partials/item-table.jsx";
import LedgerTable from "./partials/ledger/ledger-table.jsx";

import Label from "./partials/label.jsx";
import LinkLike from "./partials/link-like.jsx";
import QuillEditor from "./partials/quill-editor.jsx";

import connectToStores from "alt-utils/lib/connectToStores";
import OrderActions from "../actions/OrderActions";
import OrderStore from "../stores/OrderStore";
import UserStore from "../stores/UserStore";
import PersonStore from "../stores/PersonStore";
import PersonActions from "../actions/PersonActions";
import EventBucketStore from "../stores/EventBucketStore";
import GlobalNotificationActions from "../actions/GlobalNotificationActions";

import RefundOverlay from "./partials/overlays/refund.jsx";
import WorkingIndicator from "./partials/working-indicator.jsx";
import Notes from "./partials/notes/notes.jsx";

class Order extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      refunding: false,
      isResendingBulk: false,
      bulkCurrentlyResending: null,
      showBulkImportEditor: false,
      bulkImportMessage: ""
    };

    this.resend = this.resend.bind(this);
    this.downloadReceipt = this.downloadReceipt.bind(this);
    this.refund = this.refund.bind(this);
    this.addEmail = this.addEmail.bind(this);

    this.bulkImportLoadDetails = this.bulkImportLoadDetails.bind(this);
    this.bulkImportResend = this.bulkImportResend.bind(this);
  }

  static getStores() {
    return [EventBucketStore, OrderStore, PersonStore];
  }

  static getPropsFromStores() {
    let {
        orders,
        resendConfirmationErrorMessage,
        refundErrorMessage,
        refundSuccess
      } = OrderStore.getState(),
      { isFetching, people } = PersonStore.getState();
    return {
      orders,
      resendConfirmationErrorMessage,
      refundErrorMessage,
      refundSuccess,
      isFetchingPeople: isFetching,
      people,
      getEventBucketById: EventBucketStore.getEventBucketById
    };
  }

  static get contextTypes() {
    return {
      router: React.PropTypes.object
    };
  }

  getStyles() {
    return {
      topBarButton: {
        whiteSpace: "nowrap",
        overflowX: "hidden",
        marginLeft: 5,
        marginRight: 5
      },
      details: {
        marginTop: 20,
        marginBottom: 20,
        border: "1px solid #ccc",
        padding: 15,
        paddingTop: 25
      },
      price: {
        fontSize: 29,
        fontWeight: "bold",
        marginBottom: 3
      },
      netPrice: {
        fontSize: 18,
        color: "rgb(140, 140, 140)"
      },
      subtitle: {
        fontSize: 16
      },
      chips: {
        marginLeft: -4,
        display: "flex",
        flexWrap: "wrap"
      },
      chip: {
        margin: 4
      },
      section: {
        border: "1px solid #ccc",
        padding: 15,
        marginBottom: 20
      },
      personMain: {
        fontSize: 20,
        fontWeight: "bold"
      },
      personSub: {
        fontSize: 15
      },
      notesHeader: {
        fontSize: 20,
        fontWeight: "bold"
      },
      bulkInfo: {
        fontSize: 16
      }
    };
  }

  componentDidMount() {
    OrderActions.fetchOne(this.props.params.orderId);
  }

  resend(opts = {}) {
    this.setState({ isResending: true });
    GlobalNotificationActions.notify.defer("Resending confirmation email...");
    OrderActions.resendConfirmation(
      Object.assign(
        { id: this.props.params.orderId },
        opts.refund && { refund_confirmation: true }
      )
    );
  }

  resendGift(opts = {}) {
    this.setState({ isResending: true });
    GlobalNotificationActions.notify.defer("Resending confirmation email...");
    OrderActions.resendGiftNotification(this.props.params.orderId)
  }

  downloadReceipt() {
    OrderActions.downloadReceipt(this.props.params.orderId);
  }

  refund(amount) {
    this.setState({ refunding: false, isRefunding: true });
    GlobalNotificationActions.notify.defer("Refunding order...");
    OrderActions.refund(this.props.params.orderId, amount);
  }

  addEmail() {
    let email = window.prompt(
      "Please enter the email address to connect to the order."
    );

    if (!email) {
      return;
    }

    OrderActions.edit(this.props.params.orderId, {
      box_office_email: email
    });
  }

  bulkImportLoadDetails() {
    const bulkInfo = this.props.orders[this.props.params.orderId]._bulk_import,
      message = Object.values(bulkInfo)[0].extra_message;

    PersonActions.fetchAll({
      conditions: { _from_bulk_order: this.props.params.orderId }
    });
    delete this.props.orders[this.props.params.orderId].bulkResent;
    OrderActions.updateOrder(this.props.orders[this.props.params.orderId]);
    this.setState({
      isResendingBulk: true,
      showBulkImportEditor: !!message,
      bulkImportMessage: message
    });
  }

  async bulkImportResend(person, order) {
    if (person === "all") {
      if (
        window.confirm(
          `Are you sure? All confirmations will be resent immediately.`
        )
      ) {
        const persons = Object.keys(order._bulk_import)
          .map(k => ({
            person: this.props.people[k],
            data: order._bulk_import[k]
          }))
          .sort((a, b) =>
            (a.person.details.name || a.person.details.email).localeCompare(
              b.person.details.name || b.person.details.email
            )
          );
        for (const p of persons) {
          this.bulkImportResend(p.person.id);
          await new Promise(resolve => setTimeout(() => resolve(), 750));
        }
      }
      return;
    }

    OrderActions.resendConfirmation({
      id: this.props.params.orderId,
      bulk_person: person,
      extra_message: this.state.bulkImportMessage
    });
    this.setState({ bulkCurrentlyResending: person });
  }

  componentWillReceiveProps(newProps) {
    let order = newProps.orders[this.props.params.orderId];

    if (this.state.isRefunding && order.refunded_at && newProps.refundSuccess) {
      this.setState({ isRefunding: false });
      GlobalNotificationActions.notify.defer("Order refunded successfully.");
      OrderActions.clearErrors.defer();
    }

    if (this.state.isResending && order.confirmationResent) {
      this.setState({ isResending: false });
      GlobalNotificationActions.notify.defer(
        "Confirmation resent successfully."
      );
      OrderActions.clearErrors.defer();
    }

    if (this.state.bulkCurrentlyResending && order.bulkResent) {
      this.setState({ bulkCurrentlyResending: null });
      GlobalNotificationActions.notify.defer(
        "Activation email resent successfully."
      );
      OrderActions.clearErrors.defer();
    }

    if (newProps.resendConfirmationErrorMessage) {
      GlobalNotificationActions.notify.defer(
        `Error resending confirmation: ${newProps.resendConfirmationErrorMessage}`
      );
      OrderActions.clearErrors.defer();
    }

    if (newProps.refundErrorMessage) {
      this.setState({
        refunding: true,
        refundErrorMessage: newProps.refundErrorMessage
      });
      OrderActions.clearErrors.defer();
    }
  }

  render() {
    let person,
      styles = this.getStyles(),
      currentEventBucket = this.props.params.eventBucketId,
      order = this.props.orders[this.props.params.orderId];

    if (!order) {
      return <WorkingIndicator fullPage={true} />;
    }

    if (order.person) {
      person = order.person.details;
      person.id = order.person.id;
    }

    // Handle legacy full refunds
    if (order.refunded_at && !order.original_price) {
      order.original_price = order.purchase_price;
      order.purchase_price = 0;
    }

    let fullEventBucket = this.props.getEventBucketById(currentEventBucket);

    const isSundance = fullEventBucket && fullEventBucket.frontend_config ? fullEventBucket.frontend_config.sundance_login : false;
    const showTransferHistory = fullEventBucket && fullEventBucket.frontend_config ? fullEventBucket.frontend_config.enable_transfer_history : false;

    return (
      <div>
        {this.state.refunding ? (
          <RefundOverlay
            order={order}
            onRefund={this.refund}
            refundErrorMessage={this.state.refundErrorMessage}
            onRequestClose={() =>
              this.setState({ refunding: false, refundErrorMessage: null })
            }
          />
        ) : null}
        <Toolbar>
          <ToolbarGroup style={{ minWidth: 0 }}>
            <ToolbarTitle
              style={{ paddingRight: 0 }}
              text={`Order #${order.id}`}
            />
          </ToolbarGroup>
          <ToolbarGroup
            style={order.refunded_at ? { minWidth: 730 } : { minWidth: 300 }}
          >
            {[
              <RaisedButton
                label="Resend confirmation"
                primary={false}
                style={styles.topBarButton}
                onTouchTap={() => this.resend()}
              />
            ]
              .concat(
                order.gift_cards && order.gift_cards.length > 0 ?
                  [
                    <RaisedButton
                      label={"Resend gift notification"}
                      primary={false}
                      style={styles.topBarButton}
                      onTouchTap={() => this.resendGift()}
                    />
                  ] : []
              )
              .concat(
                order.refunded_at
                  ? [
                      <RaisedButton
                        label="Resend refund confirmation"
                        primary={false}
                        style={styles.topBarButton}
                        onTouchTap={() => this.resend({ refund: true })}
                      />
                    ]
                  : []
              )
              .concat([
                <RaisedButton
                  label="Download receipt"
                  primary={false}
                  style={styles.topBarButton}
                  onTouchTap={this.downloadReceipt}
                />
              ])
              .concat(
                order.refunded_at && order.purchase_price === 0
                  ? [
                      <RaisedButton
                        label="Refunded"
                        primary={false}
                        style={Object.assign({}, styles.topBarButton, {
                          marginRight: 0
                        })}
                        disabled={true}
                      />
                    ]
                  : [
                      <RaisedButton
                        label="Refund"
                        disabled={!UserStore.hasPermission("manage_orders")}
                        primary={false}
                        style={Object.assign({}, styles.topBarButton, {
                          marginRight: 0
                        })}
                        onTouchTap={() =>
                          this.setState({
                            refunding: true,
                            refundErrorMessage: null
                          })
                        }
                      />
                    ]
              )}
          </ToolbarGroup>
        </Toolbar>
        <div style={styles.details}>
          <div style={styles.price}>
            {formatPrice(order.purchase_price || 0)}{" "}
            <span style={styles.netPrice}>
              {formatPrice(
                order.net_price || 0,
                order.platform_managed && { currency: "usd" }
              )}{" "}
              net
              {order.tax_collected
                ? ` (${!order.platform_managed ? "+" : "incl."} ${formatPrice(
                    order.tax_collected
                  )} tax)`
                : ""}
              {order.refunded_at
                ? ` (refunded from ${formatPrice(
                    order.original_price || order.purchase_price
                  )})`
                : ""}
            </span>
          </div>
          <div style={styles.subtitle}>
            <div>{order.summary}</div>
            {order.gift_cards && order.gift_cards.length > 0
              ? <div>{order.gift_cards[0].code}</div>
              : null
            }
            <div>{moment(order.created_at).format("LLLL")}</div>
            {order.refunded_at ? (
              <div>
                <strong>
                  Refunded {moment(order.refunded_at).format("LLLL")}
                </strong>
              </div>
            ) : null}
            {order.origin_location && order.origin_location.ip ? (
              order.origin_location.city ? (
                <div>
                  Placed from{" "}
                  <span
                    title={`(${order.origin_location.latitude}, ${order.origin_location.longitude})`}
                  >
                    {order.origin_location.city}, {order.origin_location.region}{" "}
                    {order.origin_location.postal
                      ? `(${order.origin_location.postal})`
                      : ""}
                  </span>
                </div>
              ) : (
                <div>Placed from IP address {order.origin_location.ip}</div>
              )
            ) : null}
            {order.additional_message ? (
              <div>
                Additional message: <em>{order.additional_message}</em>
              </div>
            ) : null}
          </div>
          <div style={styles.chips}>
            {order.type === "kiosk" ? (
              <Chip
                style={styles.chip}
                onTouchTap={() =>
                  this.context.router.push(
                    `/event_buckets/${order.event_bucket.id}/kiosks/${order.kiosk.id}`
                  )
                }
              >
                Kiosk used: {order.kiosk.name}
              </Chip>
            ) : null}
            {order.type === "box_office" ? (
              <Chip style={styles.chip}>Box Office ({order.subtype})</Chip>
            ) : null}
            {order.type === "check_in" ? (
              <Chip style={styles.chip}>Created at Check-In</Chip>
            ) : null}
            {order.type === "admin_adjustment" ? (
              <Chip style={styles.chip}>Admin Adjustment</Chip>
            ) : null}
            {order.type === "rush_line" ? (
              <Chip style={styles.chip}>Rush Line ({order.subtype})</Chip>
            ) : null}
            {order.type === "autorenewal" ? (
              <Chip style={styles.chip}>Autorenewal</Chip>
            ) : null}
            {order.type === "bulk" ? (
              <Chip style={styles.chip}>Bulk order</Chip>
            ) : null}
            {order.type === "instant_ticket" ? (
              <Chip style={styles.chip}>Instant Ticket</Chip>
            ) : null}
            {order.coupon ? (
              <Chip
                style={styles.chip}
                onTouchTap={() =>
                  this.context.router.push(
                    `/event_buckets/${order.event_bucket.id}/coupons/${order.coupon.id}`
                  )
                }
              >
                Discount used: {order.coupon.name}{" "}
                {order.coupon.code && `(${order.coupon.code})`}
              </Chip>
            ) : null}
            {order.subtype === "check" ? (
              <Chip style={styles.chip}>
                Check # {order.check_number || "not entered"}
              </Chip>
            ) : null}
            {order.sold_by_user_account ? (
              <Chip style={styles.chip}>
                Sold by {order.sold_by_user_account.name}
              </Chip>
            ) : null}
            {order.presentment_currency ? (
              <Chip style={styles.chip}>
                Sold as{" "}
                {formatPrice(order.presentment_purchase_price, {
                  currency: order.presentment_currency
                })}{" "}
                ({order.presentment_currency.toUpperCase()})
              </Chip>
            ) : null}
            {order.tracking_data
              ? Object.keys(order.tracking_data).map(key => (
                  <Chip key={key} style={styles.chip}>
                    Tracking {key}: {order.tracking_data[key]}
                  </Chip>
                ))
              : null}
          </div>
        </div>
        {order.box_office_email ? (
          <div style={styles.section}>
            <div style={styles.personMain}>{order.box_office_email}</div>
            <div style={styles.personSub}>
              Via {order.type === "web" ? "guest checkout" : "box office"}
            </div>
          </div>
        ) : null}
        {order.type === "bulk" && order._bulk_import ? (
          <div style={styles.section}>
            <h2>Bulk import info</h2>
            <div style={styles.bulkInfo}>
              {order.passes.length}{" "}
              {order.passes.length === 1 ? "pass was" : "passes were"} imported
              and {order.passes.length === 1 ? "was" : "were"} associated with{" "}
              {Object.keys(order._bulk_import).length} unique account
              {Object.keys(order._bulk_import).length === 1 ? "" : "s"}.
            </div>
            {this.state.isResendingBulk && this.props.isFetchingPeople ? (
              <div style={styles.bulkInfo}>
                Loading available recipients...{" "}
                <LinkLike
                  onTouchTap={() => this.setState({ isResendingBulk: false })}
                >
                  Cancel
                </LinkLike>
              </div>
            ) : this.state.isResendingBulk ? (
              <div>
                <div style={styles.bulkInfo}>
                  Select a person to resend their activation email & link.{" "}
                  <LinkLike
                    onTouchTap={() => this.setState({ isResendingBulk: false })}
                  >
                    Cancel
                  </LinkLike>
                </div>
                <div style={styles.bulkInfo}>
                  You can also{" "}
                  <LinkLike
                    style={{ fontWeight: "bold" }}
                    onTouchTap={() => this.bulkImportResend("all", order)}
                  >
                    resend all {Object.keys(order._bulk_import).length} email
                    {Object.keys(order._bulk_import).length === 1 ? "" : "s"} at
                    once
                  </LinkLike>
                  .
                </div>
                <div style={{ marginLeft: "1.5em", marginBottom: "1em" }}>
                  <Label>
                    Additional note (appended to notification email)
                  </Label>
                  {this.state.showBulkImportEditor ? (
                    <QuillEditor
                      value={this.state.bulkImportMessage}
                      onChange={v => this.setState({ bulkImportMessage: v })}
                      basic={true}
                    />
                  ) : (
                    <div style={{ marginTop: -5 }}>
                      None set &ndash;{" "}
                      <LinkLike
                        onTouchTap={() =>
                          this.setState({ showBulkImportEditor: true })
                        }
                      >
                        Add additional note
                      </LinkLike>
                    </div>
                  )}
                </div>
                <ul style={{ margin: 0 }}>
                  {Object.keys(order._bulk_import)
                    .map(k => ({
                      person: this.props.people[k],
                      data: order._bulk_import[k]
                    }))
                    .sort((a, b) =>
                      (
                        a.person.details.name || a.person.details.email
                      ).localeCompare(
                        b.person.details.name || b.person.details.email
                      )
                    )
                    .map(({ person, data }) => {
                      return (
                        <li>
                          {person.details.name ? (
                            <span>
                              {person.details.name} ({person.details.email})
                            </span>
                          ) : (
                            person.details.email
                          )}
                          &nbsp;&ndash;&nbsp;
                          {data.passes.length} pass
                          {data.passes.length === 1 ? "" : "es"}
                          &nbsp;&ndash;&nbsp;
                          {order.bulkResent === person.id ? (
                            "resent successfully"
                          ) : this.state.bulkCurrentlyResending ===
                            person.id ? (
                            "resending..."
                          ) : (
                            <span
                              title={
                                data.email_sent
                                  ? `Last sent ${moment(data.email_sent).format(
                                      "lll"
                                    )}`
                                  : "Never sent"
                              }
                            >
                              <LinkLike
                                onTouchTap={this.bulkImportResend.bind(
                                  this,
                                  person.id
                                )}
                              >
                                resend{" "}
                                {!data.email_sent ? (
                                  <span
                                    style={{
                                      padding: "0 2px",
                                      backgroundColor: "red",
                                      color: "#fff"
                                    }}
                                  >
                                    never sent
                                  </span>
                                ) : null}
                              </LinkLike>
                            </span>
                          )}
                        </li>
                      );
                    })}
                </ul>
              </div>
            ) : (
              <div style={styles.bulkInfo}>
                {
                  Object.values(order._bulk_import).filter(v => v.email_sent)
                    .length
                }{" "}
                / {Object.values(order._bulk_import).length} activation emails
                sent.{" "}
                <LinkLike onTouchTap={this.bulkImportLoadDetails}>
                  Resend activation emails...
                </LinkLike>
              </div>
            )}
          </div>
        ) : null}
        {person ? (
          <div style={styles.section}>
            {person.name ? (
              <div>
                <div style={styles.personMain}>{person.name}</div>
                <div style={styles.personSub}>{person.email}</div>
              </div>
            ) : (
              <div style={styles.personMain}>{person.email}</div>
            )}
            <RaisedButton
              label="View person"
              style={{ marginTop: 5 }}
              primary={true}
              onTouchTap={() =>
                this.context.router.push(
                  `${
                    currentEventBucket
                      ? `/event_buckets/${currentEventBucket}`
                      : ""
                  }/people/${order.person.id}`
                )
              }
            />
          </div>
        ) : null}
        {isSundance && <div style={styles.section}>
          <h4 style={styles.notesHeader}>Notes</h4>
          <Notes order={order.id} />
        </div>}
        {!order.box_office_email && !person && order.type !== "bulk" ? (
          <div style={styles.section}>
            <div style={styles.personSub}>No email address connected.</div>
            <RaisedButton
              label="Add email address"
              style={{ marginTop: 5 }}
              primary={true}
              onTouchTap={this.addEmail}
            />
          </div>
        ) : null}
        {order.tickets.length ? (
          <div style={styles.section}>
            <h2>Tickets</h2>
            <TicketTable
              tickets={order.tickets}
              person={order.person && order.person.id}
              eventBucket={order.event_bucket.id}
              isSundance={isSundance}
              showTransferHistory={showTransferHistory}
              order={order.id}
              showEvent={true}
              location={this.props.location}
            />
          </div>
        ) : null}
        {order.passes.length ? (
          <div style={styles.section}>
            <h2>Passes</h2>
            <PassTable
              passes={order.passes}
              person={order.person && order.person.details}
              order={order.id}
              eventBucket={order.event_bucket.id}
              isSundance={isSundance}
              showTransferHistory={showTransferHistory}
              isSinglePerson={!!person}
              location={this.props.location}
            />
          </div>
        ) : null}
        {order.items.length ? (
          <div style={styles.section}>
            <h2>Items</h2>
            <ItemTable
              items={order.items}
              person={order.person && order.person.details}
              order={order.id}
              eventBucket={order.event_bucket.id}
              showItemBucket={true}
              isSinglePerson={!!person}
              location={this.props.location}
            />
          </div>
        ) : null}
        {order.in_ledger ? (
          <div style={styles.section}>
            <h2 style={{ marginBottom: "0.5em" }}>
              Ledger transactions {/*<InlineNoticeLabel label="Preview" />*/}
            </h2>
            {/* <p style={{ fontStyle: "italic" }}>
              You have been invited to the early-access Eventive Ledger Preview.
              Eventive Ledger powers the next generation of Eventive financial
              reporting tools and will be launching soon. Some features are not
              yet fully supported &ndash; as an early-access tester, please{" "}
              <a
                href="#"
                onClick={e => {
                  e.preventDefault();
                  window.Intercom("showNewMessage");
                }}
              >
                let us know
              </a>{" "}
              if anything seems off!
            </p> */}
            <LedgerTable orderId={order.id} />
          </div>
        ) : null}
      </div>
    );
  }
}

export default connectToStores(Order);



// WEBPACK FOOTER //
// ./src/app/components/order.jsx